/* eslint-disable @next/next/no-img-element -- The source design sizes these intrinsically and animates them directly in the DOM. */
import { Fragment, type ReactNode } from 'react';
import Link from 'next/link';
import { LandingBackToTop } from '@/app/shared/landing/components/landing-back-to-top';
import {
  LandingSourceFooter,
  type LandingFooterCta,
} from '@/app/shared/landing/components/landing-source-footer';
import { LandingSourceHeader } from '@/app/shared/landing/components/landing-source-header';
import { LandingPageEffects } from '@/app/shared/landing/pages/landing-page-effects';
import {
  LandingFaqJsonLd,
  LandingPageJsonLd,
} from '@/app/shared/landing/pages/components/landing-json-ld';
import type { LandingSection } from '@/app/shared/landing/pages/landing-route-seo';
import { CarrierComplianceSection } from '@/app/shared/landing/components/carrier-compliance-section';
import '@/app/shared/landing/home/styles/grid-utilities.css';
import '@/app/shared/landing/home/styles/source-design.css';
import '@/app/shared/landing/pages/styles/ship-smart-pages.css';
import '@/app/shared/landing/pages/styles/sub-page-dark.css';
import '@/app/shared/landing/styles/landing-tweaks.css';
import '@/app/shared/landing/styles/mobile-drawer.css';

/**
 * The nine blocks every landing sub-page is built from. The markup here is a direct port
 * of the static source pages in ~/Desktop/LightShipSmart, so the class names and their
 * order must stay byte-identical to the design, see ../AGENTS.md before changing any.
 */

export type LinkSpec = { label: string; href: string };
export type Proof = readonly [value: string, label: string];
export type Card = readonly [title: string, copy: string, delay: string];
export type CompareColumn = readonly [
  label: string,
  heading: string,
  items: readonly string[],
];
export type Diagnostic = readonly [heading: string, copy: ReactNode];
export type Phase = readonly [title: string, copy: string, delay: string];
export type Faq = readonly [question: string, answer: string];

/** The template's corner arrow, doubled inside `.at-btn` for its hover swap. */
export function CornerArrow({ fill = 'currentColor' }: { fill?: string }) {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 11 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0.21967 9.40717C-0.0732232 9.70006 -0.0732232 10.1749 0.21967 10.4678C0.512563 10.7607 0.987437 10.7607 1.28033 10.4678L0.21967 9.40717ZM10.6875 0.75C10.6875 0.335786 10.3517 2.97145e-09 9.9375 1.50485e-07L3.1875 -2.70983e-07C2.77329 -2.70983e-07 2.4375 0.335786 2.4375 0.75C2.4375 1.16421 2.77329 1.5 3.1875 1.5H9.1875V7.5C9.1875 7.91421 9.52329 8.25 9.9375 8.25C10.3517 8.25 10.6875 7.91421 10.6875 7.5L10.6875 0.75ZM0.75 9.9375L1.28033 10.4678L10.4678 1.28033L9.9375 0.75L9.40717 0.21967L0.21967 9.40717L0.75 9.9375Z"
        fill={fill}
      />
    </svg>
  );
}

export function CtaArrow({ fill = 'currentColor' }: { fill?: string }) {
  return (
    <svg
      fill="none"
      height="13"
      viewBox="0 0 14 13"
      width="14"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.0037 3.41421L2.39712 12.0208L0.98291 10.6066L9.5895 2H2.00373V0H13.0037V11H11.0037V3.41421Z"
        fill={fill}
      />
    </svg>
  );
}

export function PageTitleEyebrow({
  eyebrow,
  className,
}: {
  eyebrow: string;
  className?: string;
}) {
  return (
    <div className="col-12">
      <span
        aria-label={eyebrow}
        className={`at-btn common-black rounded-0 mb-10 bg-transparent p-0 ${className || ''}`}
      >
        <span aria-hidden="true" className="text-uppercase">
          <span className="text-1">{eyebrow}</span>
          <span className="text-2">{eyebrow}</span>
        </span>
        <i>
          <CornerArrow />
          <CornerArrow />
        </i>
      </span>
    </div>
  );
}

function CircleArrow() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="15"
      viewBox="0 0 16 15"
      fill="none"
    >
      <path
        d="M0.0001297 8.99993L0 3.00407e-05L2 0L2.0001 6.99993L12.1719 7.00003L8.22224 3.05027L9.63644 1.63606L16.0003 8.00003L9.63644 14.364L8.22224 12.9497L12.1719 9.00003L0.0001297 8.99993Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function Pill({ label }: { label: string }) {
  return (
    <span
      className="sec-2-home-13__pill at_fade_anim mb-3"
      data-fade-from="bottom"
      data-delay=".05"
    >
      <span className="sec-2-home-13__pill-dot" aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

function SectionHead({
  pill,
  eyebrow,
  heading,
  intro,
}: {
  pill?: string;
  eyebrow?: string;
  heading: string;
  intro?: ReactNode;
}) {
  return (
    <div className="ss-section-head">
      <div>
        {pill ? <Pill label={pill} /> : null}
        {eyebrow ? (
          <span className="ss-eyebrow text-white">{eyebrow}</span>
        ) : null}
        <h2>{heading}</h2>
      </div>
      {intro ? <p>{intro}</p> : null}
    </div>
  );
}

export function SubPageTitle({
  eyebrow,
  title,
  titleAccent,
}: {
  eyebrow: string;
  title: string;
  /** Services pages split the h1 over two lines, the second in the brand blue. */
  titleAccent?: string;
}) {
  return (
    <section className="ss-page-title">
      <div className="container">
        <div className="row g-3">
          <PageTitleEyebrow eyebrow={eyebrow} />
          <div className="col-lg-12">
            <h1 className="fz-ds-1 lh-1 fw-500 mb-0">
              <span className="d-block ss-page-title__lead">{title}</span>
              {titleAccent ? (
                <>
                  {' '}
                  <span className="d-block text-base">{titleAccent}</span>
                </>
              ) : null}
            </h1>
          </div>
        </div>
      </div>
    </section>
  );
}

export function SubPageHero({
  ariaLabel,
  image,
  imageAlt,
  caption,
  headline,
  copy,
  cta,
  secondaryCta,
  proofHeading,
  proofs,
}: {
  ariaLabel: string;
  image: string;
  imageAlt: string;
  caption: string;
  headline: string;
  copy: string;
  cta: LinkSpec;
  secondaryCta?: LinkSpec;
  proofHeading?: string;
  proofs: readonly Proof[];
}) {
  return (
    <section className="sec-2-home-14" aria-label={ariaLabel}>
      <div className="sec-2-home-14__inner">
        <div className="sec-2-home-14__main">
          <div className="sec-2-home-14__portrait-col">
            <figure className="sec-2-home-14__portrait anim-zoomin-wrap mb-0">
              <img
                className="sec-2-home-14__portrait-img anim-zoomin rounded-3"
                src={image}
                alt={imageAlt}
                loading="lazy"
                decoding="async"
              />
              {caption ? (
                <figcaption className="sec-2-home-14__portrait-overlay">
                  <p className="sec-2-home-14__portrait-tag mb-0" />
                  <div className="sec-2-home-14__portrait-caption">
                    <p className="sec-2-home-14__portrait-name fs-2 fw-600 mb-0 text-white">
                      {caption}
                    </p>
                  </div>
                </figcaption>
              ) : null}
            </figure>
          </div>

          <div className="sec-2-home-14__manifesto-col">
            <h2 className="ss-hero__headline">{headline}</h2>
            <p
              className="ss-hero__copy"
              data-fade-from="bottom"
              data-delay=".2"
            >
              {copy}
            </p>
            <div
              className="sec-2-home-14__signature ss-actions at_fade_anim pt-50"
              data-fade-from="bottom"
              data-delay=".3"
            >
              <Link className="card-home-12-price__cta bg-base" href={cta.href}>
                <span className="card-home-12-price__cta-text">
                  {cta.label}
                </span>
                <span
                  aria-hidden="true"
                  className="card-home-12-price__cta-icon"
                >
                  <CtaArrow />
                </span>
              </Link>
              {secondaryCta ? (
                <Link
                  className="card-home-12-price__cta ss-btn-outline"
                  href={secondaryCta.href}
                >
                  <span className="card-home-12-price__cta-text">
                    {secondaryCta.label}
                  </span>
                  <span
                    aria-hidden="true"
                    className="card-home-12-price__cta-icon"
                  >
                    <CtaArrow />
                  </span>
                </Link>
              ) : null}
            </div>
          </div>
        </div>

        {proofHeading ? (
          <h2 className="ss-hero__proof-heading">{proofHeading}</h2>
        ) : null}
        <ul className="sec-2-home-14__stats list-unstyled mb-0">
          {proofs.map(([value, label], index) => (
            <li
              className="sec-2-home-14__stat at_fade_anim"
              data-fade-from="bottom"
              data-delay={`.${index + 1}`}
              key={value}
            >
              <div className="ss-proof__value">{value}</div>
              <p className="ss-proof__label">{label}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * `layout` mirrors the only structural difference between the source sub-pages:
 * managed-transportation wraps the cards in `.sec-3-home-12__grid`, freight-brokerage
 * uses the Bootstrap `row g-3 > col-md-4` grid. The card markup is identical either way.
 */
export function SubPageCardGrid({
  pill,
  heading,
  intro,
  cards,
  footer,
  cardHref,
  cardAction,
  layout,
  className,
}: {
  pill: string;
  heading: string;
  intro?: ReactNode;
  cards: readonly Card[];
  footer?: string;
  cardHref: string;
  cardAction: string;
  layout: 'grid' | 'bootstrap';
  className?: string;
}) {
  const items = cards.map(([title, copy, delay], index) => (
    <Link
      className="card-home-12-service at_fade_anim"
      data-fade-from="bottom"
      data-delay={delay}
      href={cardHref}
      aria-label={`${title}, ${cardAction}`}
      key={title}
    >
      <div className="card-home-12-service__top">
        <div className="card-home-12-service__num-wrap">
          <span className="card-home-12-service__num">
            {String(index + 1).padStart(2, '0')}
          </span>
        </div>
      </div>
      <h3 className="card-home-12-service__title">{title}</h3>
      <p className="card-home-12-service__desc">{copy}</p>
    </Link>
  ));

  return (
    <section className={`ss-section ${className ?? ''}`}>
      <div className="container">
        <SectionHead pill={pill} heading={heading} intro={intro} />
        {layout === 'grid' ? (
          <div className="sec-3-home-12__grid">{items}</div>
        ) : (
          <div className="row g-3">
            {items.map((item, index) => (
              <div className="col-md-4" key={cards[index][0]}>
                {item}
              </div>
            ))}
          </div>
        )}
        {footer ? <p className="ss-section-note">{footer}</p> : null}
      </div>
    </section>
  );
}

export function SubPageCompare({
  eyebrow,
  heading,
  intro,
  columns,
  footer,
}: {
  eyebrow: string;
  heading: string;
  intro: string;
  columns: readonly CompareColumn[];
  footer?: string;
}) {
  return (
    <section className="ss-section ss-section--dark">
      <div className="container">
        <SectionHead eyebrow={eyebrow} heading={heading} intro={intro} />
        <div className={`ss-compare ss-compare--${columns.length}`}>
          {columns.map(([label, columnHeading, items]) => (
            <div className="ss-compare__col" key={columnHeading || label}>
              {label ? <div className="ss-compare__label">{label}</div> : null}
              {columnHeading ? <h3>{columnHeading}</h3> : null}
              <ul>
                {items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {footer ? <p className="ss-section-note">{footer}</p> : null}
      </div>
    </section>
  );
}

export function SubPageDiagnostic({
  sectionClassName = '',
  pill,
  heading,
  intro,
  items,
  tags,
  soft = false,
  dark = false,
}: {
  sectionClassName?: string;
  pill: string;
  heading: string;
  intro?: ReactNode;
  items: readonly Diagnostic[];
  tags: readonly LinkSpec[];
  soft?: boolean;
  dark?: boolean;
}) {
  return (
    <section
      className={`${
        dark
          ? 'ss-section ss-section--dark'
          : soft
            ? 'ss-section ss-section--soft'
            : 'ss-section'
      } ${sectionClassName}`.trim()}
    >
      <div className="container">
        <SectionHead pill={pill} heading={heading} intro={intro} />
        <div className="ss-diagnostic">
          {items.map(([itemHeading, copy], index) => (
            <div
              className={
                index === items.length - 1
                  ? 'ss-diagnostic__item border-bottom-0'
                  : 'ss-diagnostic__item'
              }
              key={itemHeading}
            >
              <span className="ss-diagnostic__num">
                {String(index + 1).padStart(2, '0')}
              </span>
              <div>
                <h3>{itemHeading}</h3>
                <p>{copy}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="ss-tags">
          {tags.map((tag) => (
            <Link className="ss-tag" href={tag.href} key={tag.label}>
              {tag.label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SubPageProcess({
  eyebrow,
  headingLead,
  headingAccent,
  copy,
  cta,
  phases,
  stepSection = false,
  headingInline = false,
}: {
  eyebrow: string;
  headingLead: string;
  headingAccent: string;
  copy: string;
  cta: LinkSpec;
  phases: readonly Phase[];
  /** Services pages run this section on the dark `.ss-step-section` background. */
  stepSection?: boolean;
  /** Most sources break the heading before the accent; short ones keep it on one line. */
  headingInline?: boolean;
}) {
  return (
    <section
      className={
        stepSection ? 'sec-5-home-15 ss-step-section' : 'sec-5-home-15'
      }
      aria-label="Our process"
    >
      <div className="container">
        <div className="row gx-4 gy-4 align-items-end mb-65">
          <div className="col-xxl-8 col-xl-7 col-lg-7 col-12">
            <span
              aria-label={eyebrow}
              className="at-btn common-black text-uppercase rounded-0 mb-10 bg-transparent p-0"
            >
              <span aria-hidden="true" className="text-uppercase">
                <span className="text-1 text-light">{eyebrow}</span>
                <span className="text-2 text-light">{eyebrow}</span>
              </span>
              <i>
                <CornerArrow fill="#ffffff" />
                <CornerArrow fill="#ffffff" />
              </i>
            </span>
            <h2 className="sec-5-home-15__heading reveal-text page-title">
              {headingInline ? `${headingLead} ` : headingLead}
              {headingInline ? null : <br />}
              <span className="text-base">{headingAccent}</span>
            </h2>
          </div>
          <div className="col-xxl-4 col-xl-5 col-lg-5 col-12">
            <div
              className="sec-5-home-15__intro at_fade_anim"
              data-fade-from="bottom"
              data-delay=".2"
            >
              {copy ? <p className="sec-5-home-15__copy">{copy}</p> : null}
              <div
                className="at-btn-group sec-5-home-15__cta at_fade_anim"
                data-delay=".3"
                data-fade-from="bottom"
                data-ease="bounce"
              >
                <Link
                  aria-hidden="true"
                  className="at-btn-circle"
                  href={cta.href}
                  tabIndex={-1}
                >
                  <CircleArrow />
                </Link>
                <Link className="at-btn z-index-1" href={cta.href}>
                  {cta.label}
                </Link>
                <Link
                  aria-hidden="true"
                  className="at-btn-circle"
                  href={cta.href}
                  tabIndex={-1}
                >
                  <CircleArrow />
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="row row-cols-xxl-4 row-cols-xl-4 row-cols-lg-3 row-cols-md-2 row-cols-1 gx-0 gy-0 sec-5-home-15__phases">
          {phases.map(([title, phaseCopy, delay], index) => (
            <div className="col" key={title}>
              <article
                className="sec-5-home-15__phase at_fade_anim"
                data-fade-from="bottom"
                data-delay={delay}
              >
                <span className="sec-5-home-15__phase-dot" aria-hidden="true" />
                <p className="sec-5-home-15__phase-num">
                  {String(index + 1).padStart(2, '0')}
                </p>
                <p className="sec-5-home-15__phase-title">{title}</p>
                <p className="sec-5-home-15__phase-desc">{phaseCopy}</p>
              </article>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Identical on every sub-page, so the copy lives here rather than in each page. */
const marqueeItems = [
  'Managed Transportation',
  'Dedicated Capacity',
  'Freight Brokerage',
  'Real-Time Tracking',
  'Warehousing & Distribution',
  'Expedited Freight',
  'Reefer & Dry Van',
  'Specialized Logistics',
  'Nationwide Coverage',
  'Last-Mile Delivery',
  'Reliable On-Time Delivery',
];

/** One pass of the marquee. Rendered twice so the loop has no visible seam. */
function MarqueeSequence({ pass, items }: { pass: string; items: string[] }) {
  return (
    <>
      {items.map((item) => (
        <Fragment key={`${pass}-${item}`}>
          <li className="sec-2-home-8__item">{item}</li>
          <li className="sec-2-home-8__sep">
            <img
              src="/landing/assets/images/pages/home-7/marquee-star.svg"
              alt=""
            />
          </li>
        </Fragment>
      ))}
    </>
  );
}

/** `items` only needs passing on pages whose source marquee differs from the default. */
export function SubPageMarquee({ items = marqueeItems }: { items?: string[] }) {
  return (
    <div className="sec-2-home-8">
      <div className="sec-2-home-8__row">
        <div className="sec-2-home-8__ticker changeless carouselTicker carouselTicker-left py-3">
          <ul className="d-flex align-items-center carouselTicker__list scroll-move-left list-unstyled sec-2-home-8__list mb-0">
            <MarqueeSequence pass="a" items={items} />
            <MarqueeSequence pass="b" items={items} />
          </ul>
        </div>
      </div>
      <div className="sec-2-home-8__rules" aria-hidden="true" />
    </div>
  );
}

export function SubPageFaq({
  pill,
  heading,
  items,
  label = 'pill',
  defaultOpenFirst = true,
}: {
  pill: string;
  heading: string;
  items: readonly Faq[];
  /**
   * The source labels this header two ways, a dotted pill on some pages, the plain
   * uppercase `.sec-8-home-12__eyebrow` on the rest. Follow the page being ported.
   */
  label?: 'pill' | 'eyebrow';
  /** Some Figma comps show every FAQ row collapsed on load. */
  defaultOpenFirst?: boolean;
}) {
  return (
    <section
      aria-label="Frequently Asked Questions"
      className="sec-8-home-12 ss-faq"
    >
      <div className="container">
        {/* Built from `items`, so the rendered questions and the schema cannot drift. */}
        <LandingFaqJsonLd items={items} />
        <header className="sec-8-home-12__header">
          {label === 'eyebrow' ? (
            <span className="sec-8-home-12__eyebrow">{pill}</span>
          ) : (
            <Pill label={pill} />
          )}
          <h2 className="sec-8-home-12__title mb-0">{heading}</h2>
        </header>
        {/* Native <details>, as in the source design: the template styles the open state
            through `.sec-8-home-12__item[open]`, which only ever matches a real details
            element, and the source gives these items no scroll animation. This is the
            documented exception to the RizzUI rule in the root AGENTS.md. */}
        <div className="sec-8-home-12__list">
          {items.map(([question, answer], index) => (
            <details
              className="sec-8-home-12__item"
              open={defaultOpenFirst && index === 0}
              key={question}
            >
              <summary className="sec-8-home-12__summary">
                <span className="sec-8-home-12__question">{question}</span>
                <span aria-hidden="true" className="sec-8-home-12__toggle">
                  <span className="sec-8-home-12__bar sec-8-home-12__bar--h" />
                  <span className="sec-8-home-12__bar sec-8-home-12__bar--v" />
                </span>
              </summary>
              <div className="sec-8-home-12__answer">
                <span aria-hidden="true" className="sec-8-home-12__divider" />
                <p>{answer}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SubPageCta({
  eyebrow,
  heading,
  copy,
  primary,
  secondary,
}: {
  eyebrow: string;
  heading: string;
  copy: string;
  primary: LinkSpec;
  secondary: LinkSpec;
}) {
  return (
    <section className="ss-cta bg-base">
      <div className="container">
        <div className="ss-cta__inner">
          <div>
            <span className="ss-eyebrow text-white">{eyebrow}</span>
            <h2>{heading}</h2>
            <p>{copy}</p>
          </div>
          <div className="ss-actions">
            <Link
              className="card-home-12-price__cta ss-cta__primary"
              href={primary.href}
            >
              <span className="card-home-12-price__cta-text">
                {primary.label}
              </span>
              <span aria-hidden="true" className="card-home-12-price__cta-icon">
                <CtaArrow />
              </span>
            </Link>
            <Link
              className="card-home-12-price__cta ss-btn-outline ss-cta__secondary"
              href={secondary.href}
            >
              <span className="card-home-12-price__cta-text">
                {secondary.label}
              </span>
              <span aria-hidden="true" className="card-home-12-price__cta-icon">
                <CtaArrow />
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * The shared page shell. The header must render inside `.light-ship-smart-root` because
 * setupHomeNavigation binds the drawer by querying within that root, and `px-blur-bottom`
 * is a page-level fixed overlay the source design places outside <main>.
 *
 * Only <main> and the footer go inside `#smooth-content`: ScrollSmoother transforms that
 * element, which would drag the fixed header, blur overlay and back-to-top along with it.
 * The source keeps exactly those three outside the wrapper for the same reason.
 */
export function SubPageShell({
  children,
  footerCta,
  showFooterCta = true,
  seo,
  mainClassName,
  rootClassName,
}: {
  children: ReactNode;
  /** Each page supplies its own footer CTA copy; omitted falls back to the home text. */
  footerCta?: LandingFooterCta;
  /** Allows a page to keep the shared footer while omitting its newsletter CTA. */
  showFooterCta?: boolean;
  /** Identifies the route so the Service and BreadcrumbList schemas can be emitted. */
  seo?: { locale: string; section: LandingSection; slug: string };
  /** Extra class on `<main>` for pages whose stylesheet scopes itself there (`guides`). */
  mainClassName?: string;
  /** Page-specific scope for theme variants that also need to repaint the shared header. */
  rootClassName?: string;
}) {
  return (
    <div
      className={[
        'light-ship-smart-root',
        seo ? 'sub-page-root' : '',
        rootClassName ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {seo ? <LandingPageJsonLd {...seo} /> : null}
      <LandingPageEffects />
      <LandingSourceHeader />
      <div className="px-blur-bottom" />
      <div id="smooth-wrapper">
        <div id="smooth-content" className="z-index-3">
          <main
            className={['bg-neutral-0', mainClassName ?? '']
              .filter(Boolean)
              .join(' ')}
          >
            {children}
            {seo?.section === 'carriers' ? <CarrierComplianceSection /> : null}
          </main>
          <LandingSourceFooter cta={footerCta} showCta={showFooterCta} />
        </div>
      </div>
      <LandingBackToTop />
    </div>
  );
}
